<script setup>
import { i18n } from 'boot/i18n';
import {
  HrAutocompleteField,
  HrDragHandle,
  HrErrorBanner,
  HrFormSectionHeader,
  HrIconDestructiveBtn,
  HrIconHintBtn,
  HrListAddBtn,
  HrListSelect,
  HrSortableList,
  HrWarningBanner,
} from 'src/design-system';
import { computed, ref } from 'vue';

import {
  CONDITION_FACTS,
  MODIFIER_ROLES,
  RULE_OUTCOMES,
} from '../../domain/constants/modifierRule';
import {
  emptyRuleRow,
  ruleModifierCodes,
  unreachableRuleRows,
  withCondition,
} from '../../domain/services/modifierRules';
import { incompleteRuleRows, isRuleRowAuthorable } from '../presenters/modifierRuleAuthoring';
import { credentialModifierOptions } from '../presenters/modifierOptions';
import {
  ANY,
  carryRuleRowKey,
  credentialOptions,
  credentialValueOf,
  degreeOptions,
  degreeValueOf,
  ruleRowKey,
} from '../presenters/modifierRuleRows';

/**
 * A payer's conditional modifiers for one covered service: which tier earns which modifier.
 *
 * ORDER IS THE CONTROL, which is why this is a sortable list rather than a table of independent
 * rows. Rows are evaluated top to bottom and the first match wins, so moving one changes what gets
 * billed — a BCBA row above a BCBA-D row makes the second unreachable, because every BCBA-D clears
 * the first row's floor too. The position number is shown for that reason and not as decoration.
 *
 * TWO DROPDOWNS, NOT A CONDITION BUILDER. The stored row holds a list of conditions so that place of
 * service, member age and the rest can arrive without a schema change, but a payer sheet is read by
 * a billing administrator and the two questions it actually asks are which credential and which
 * degree. A generic builder would price the common case at the rarest one's complexity.
 *
 * AND ACROSS THE COLUMNS, OR DOWN THE ROWS. Both dropdowns on one row must hold for it to match;
 * saying "either" is a second row producing the same modifier. That is the whole expressive range of
 * the sheets reviewed — including the ones requiring a master's AND a BCBA, which an "axis" model
 * could not state at all — and it needs no operator anywhere on the screen.
 *
 * "ANY" IS AN OPTION RATHER THAN AN EMPTY FIELD, because a row keyed only to the degree is a real
 * sheet and a blank select reads as unfinished work.
 *
 * IT SITS ABOVE THE PAYER'S BASE MODIFIER FIELD, in the order the two reach a claim line: what the
 * performer earned leads, the payer's unconditional set follows, and the telehealth modifier is last.
 * The screen reading in a different order to the line would quietly teach the wrong thing about which
 * modifier comes first, which is a requirement some payers reject a claim over.
 */
defineOptions({ name: 'ModifierRuleEditor' });

const props = defineProps({
  /** The payer's ordered rows for this service. */
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits({
  'update:modelValue': null,
});

/**
 * A `modifierRules` that is present but is not a list — a payer configured by a newer build.
 *
 * SHOWN AND LOCKED rather than rendered as empty, because an empty list is a lie that the next
 * click acts on: Add would emit a real array over the stored value, and a row nobody typed in is
 * dropped on write, so the payer's configuration would be gone without anybody seeing it go.
 */
const isUnreadable = computed(() => {
  return props.modelValue !== undefined && props.modelValue !== null && !Array.isArray(props.modelValue);
});

const rows = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));

const incomplete = computed(() => new Set(incompleteRuleRows(rows.value)));
const unreachable = computed(() => new Set(unreachableRuleRows(rows.value)));

/**
 * Rows whose modifier field somebody has visited and left, keyed by the row rather than its position
 * so that reordering does not move the mark onto a different rule.
 */
const touchedRows = ref(new Set());

const credentials = computed(() => credentialOptions());
const degrees = computed(() => degreeOptions());
const modifiers = computed(() => credentialModifierOptions());

/**
 * Rows this editor can draw in full, and rows carrying something it cannot — `isRuleRowAuthorable`,
 * which the save check reads too, so the two cannot drift into refusing a save over an uneditable
 * row. `[0]` is safe for the same reason: an authorable row carries at most one modifier.
 */
const editableRows = computed(() => {
  return rows.value.map((row, index) => ({
    // What the sortable list keys on. Without it every row renders under the same key, the framework
    // cannot tell which one moved, and a reorder snaps back to the order it started in.
    key: ruleRowKey(row, index),
    row,
    index,
    isEditable: isRuleRowAuthorable(row),
    modifier: ruleModifierCodes(row)[0] ?? '',
  }));
});

/**
 * The rows to actually complain about.
 *
 * A ROW IS BUILT LEFT TO RIGHT, so the moment a credential is chosen the row is incomplete by
 * definition — it has a condition and no modifier yet. Flagging on that state alone paints the third
 * field red while somebody is still filling in the first, which is scolding them for being partway
 * through. Waiting until the modifier field itself has been visited and left empty says the same
 * thing at the point it becomes true: they had their chance at it and moved on.
 *
 * Save stays refused the whole time regardless, because the data genuinely is not saveable. What
 * explains that while a row is still being built is the covered-service row's own caption, which
 * names an unfinished rule without needing anything to be red.
 */
const flagged = computed(() => {
  return new Set(editableRows.value
    .filter((entry) => incomplete.value.has(entry.index) && touchedRows.value.has(entry.key))
    .map((entry) => entry.index));
});

function replace(index, row) {
  // The replacement inherits the key of the row it replaces, so editing a field does not read as a
  // different row arriving — which would tear down the control being typed into.
  const next = carryRuleRowKey(rows.value[index], row);

  emit('update:modelValue', rows.value.map((existing, position) => (position === index ? next : existing)));
} // replace()

function setCredential(index, value) {
  replace(index, withCondition(rows.value[index], CONDITION_FACTS.CREDENTIAL, value === ANY ? '' : value));
} // setCredential()

function setDegree(index, value) {
  replace(index, withCondition(rows.value[index], CONDITION_FACTS.DEGREE_LEVEL, value === ANY ? '' : value));
} // setDegree()

/**
 * The modifier a row produces, as a one-entry list tagged `rate` — the tier that sets what the line
 * pays. The stored shape takes several tagged modifiers; nothing here offers that yet. The tag is
 * what the line is ordered by, so writing it keeps the ordering rule free of any particular code.
 */
function setModifier(index, code) {
  const clean = typeof code === 'string' ? code.trim().toUpperCase() : '';
  const row = rows.value[index];

  replace(index, {
    ...row,
    result: {
      outcome: RULE_OUTCOMES.BILL,
      modifiers: clean.length === 0 ? [] : [{ code: clean, role: MODIFIER_ROLES.RATE }],
    },
  });
} // setModifier()

function addRow() {
  emit('update:modelValue', rows.value.concat([emptyRuleRow()]));
} // addRow()

function removeRow(index) {
  emit('update:modelValue', rows.value.filter((row, position) => position !== index));
} // removeRow()

function markTouched(key) {
  touchedRows.value.add(key);
} // markTouched()

function reorder(next) {
  emit('update:modelValue', next.map((entry) => entry.row));
} // reorder()
</script>

<template>
  <div class="row q-col-gutter-md">
    <!-- THE EXPLANATION RIDES THE HEADER rather than sitting under it, and sits at the TRAILING edge
         where the place-of-service list next door puts its own: the words are there for somebody
         wondering how the list works, not for everybody every time they open a service. Spelled out
         permanently, three sentences of mechanism sat between the heading and the only control most
         payers need. The header's spacing is deltas on this grid's 16px gutter, so it has to be a
         child of one — which is also why the button is aligned by the row rather than by a margin. -->
    <div class="col-12">
      <div class="row items-center justify-between no-wrap">
        <hr-form-section-header>
          {{ i18n.t('settings.fundingSources.modifierRules.titleByPerformer') }}
        </hr-form-section-header>
        <hr-icon-hint-btn
          :hint="i18n.t('settings.fundingSources.modifierRules.hintSetup')"
          data-e2e="btn-modifier-rules-hint"
        />
      </div>
    </div>

    <!-- ABSENT RATHER THAN EMPTY when the payer has no rules, which is the ordinary state. An empty
         sortable list still occupies a grid cell, so the add row sat a gutter and a dead element
         below the heading instead of directly under it the way the place-override list does. -->
    <div
      v-if="rows.length > 0"
      class="col-12"
    >
      <hr-sortable-list
        :model-value="editableRows"
        item-key="key"
        @update:model-value="reorder"
      >
        <template #item="{ element }">
          <div class="row q-col-gutter-sm items-center q-pb-sm">
            <div class="col-auto">
              <hr-drag-handle />
            </div>
            <div class="col-12 col-sm">
              <hr-list-select
                :model-value="credentialValueOf(element.row)"
                :options="credentials"
                :label="i18n.t('settings.fundingSources.modifierRules.inputCredential')"
                :disable="!element.isEditable"
                hide-bottom-space
                data-e2e="select-modifier-rule-credential"
                @update:model-value="setCredential(element.index, $event)"
              />
            </div>
            <div class="col-12 col-sm">
              <hr-list-select
                :model-value="degreeValueOf(element.row)"
                :options="degrees"
                :label="i18n.t('settings.fundingSources.modifierRules.inputDegree')"
                :disable="!element.isEditable"
                hide-bottom-space
                data-e2e="select-modifier-rule-degree"
                @update:model-value="setDegree(element.index, $event)"
              />
            </div>
            <div class="col-12 col-sm-3">
              <hr-autocomplete-field
                :model-value="element.modifier"
                :options="modifiers"
                :label="i18n.t('settings.fundingSources.modifierRules.inputModifier')"
                :disable="!element.isEditable"
                :error="flagged.has(element.index)"
                hide-bottom-space
                data-e2e="input-modifier-rule-modifier"
                @blur="markTouched(element.key)"
                @update:model-value="setModifier(element.index, $event)"
              />
            </div>
            <div class="col-auto">
              <hr-icon-destructive-btn
                icon="close"
                :aria-label="i18n.t('settings.fundingSources.modifierRules.actionRemoveRow')"
                data-e2e="btn-modifier-rule-remove"
                @click="removeRow(element.index)"
              />
            </div>
          </div>
        </template>
      </hr-sortable-list>
    </div>

    <div class="col-12">
      <hr-list-add-btn
        variant="flat"
        flush
        :disable="isUnreadable"
        :label="i18n.t('settings.fundingSources.modifierRules.actionAddRow')"
        data-e2e="btn-modifier-rule-add"
        @click="addRow"
      />
    </div>

    <!-- THE FAULT IS NAMED HERE RATHER THAN UNDER THE FIELD. A message beneath one field makes that
         column taller than the two beside it, so every other row's controls re-centre the moment a
         modifier is cleared — the list jumps while somebody is editing it. The field still goes red,
         which is what says WHICH row; this says what is wrong with it. -->
    <div
      v-if="flagged.size > 0"
      class="col-12"
    >
      <hr-error-banner>
        {{ i18n.t('settings.fundingSources.modifierRules.errorRulesIncomplete') }}
      </hr-error-banner>
    </div>

    <!-- Saying so is the whole fix: the list looks empty, so without this the payer reads as having
         no rules and the next Add replaces what is actually stored. -->
    <div
      v-if="isUnreadable"
      class="col-12"
    >
      <hr-warning-banner>
        {{ i18n.t('settings.fundingSources.modifierRules.warningUnreadable') }}
      </hr-warning-banner>
    </div>

    <!-- Shown rather than prevented at the drop, because the fix is sometimes to move the catch-all
         rather than the row that ended up beneath it, and a refused drag does not say which. -->
    <div
      v-if="unreachable.size > 0"
      class="col-12"
    >
      <hr-warning-banner>
        {{ i18n.t('settings.fundingSources.modifierRules.warningUnreachable') }}
      </hr-warning-banner>
    </div>
  </div>
</template>
